// Shared snap-to-edge math for the timeline — clip drag/resize (TrackClip)
// and the scene-duration marker both snap to the same kind of targets
// (frame 0, other clips' start/end, the playhead) and want the same feel.

export const SNAP_PX = 8; // pixel radius within which an edge snaps to a target
// Snap threshold is clamped to an absolute frame range regardless of zoom —
// pure pixel-radius made the catchment swing wildly with pps (e.g. ~19
// frames / 0.63s at the furthest-out zoom), so landing just before/after a
// neighbor (leaving a small gap) was effectively impossible at low zoom.
export const MIN_SNAP_FRAMES = 2;
export const MAX_SNAP_FRAMES = 8; // ~0.27s @ 30fps
export const HYSTERESIS_FACTOR = 1.6; // widen-by factor before an active snap releases

export function computeSnapThresholdFrames(pps, fps) {
  return Math.min(MAX_SNAP_FRAMES, Math.max(MIN_SNAP_FRAMES, Math.round((SNAP_PX / pps) * fps)));
}

// Closest target within `threshold` frames, or null. Ties favor the first
// (caller passes targets sorted however it likes — irrelevant in practice
// since duplicate/near-duplicate targets snap to the same frame anyway).
export function findClosestSnap(value, targets, threshold) {
  let best = null;
  let bestDist = threshold + 1;
  for (const t of targets) {
    const d = Math.abs(value - t);
    if (d <= threshold && d < bestDist) {
      bestDist = d;
      best = t;
    }
  }
  return best;
}

// Sticky variant of findClosestSnap: once locked onto a target it keeps
// returning that same target until the value drifts past threshold ×
// HYSTERESIS_FACTOR away from it, instead of re-picking the nearest target
// from raw distance on every call — prevents flicker right at the boundary
// of a snap zone. `sticky` is a plain `{ current: frame|null }` box the
// caller owns for the lifetime of one drag gesture.
export function resolveStickySnap(value, targets, threshold, sticky) {
  if (sticky.current != null && Math.abs(value - sticky.current) <= threshold * HYSTERESIS_FACTOR) {
    return sticky.current;
  }
  const next = findClosestSnap(value, targets, threshold);
  sticky.current = next;
  return next;
}
